import type { Task, TaskId, Category, SubtaskId, EventId, Priority } from "../../../shared/types";
import { createTask } from "../../../shared/types";
import { track } from "../../../lib/analytics";
import { incrementTaskCompleted, checkTimeAchievement } from "../../gamification/store/gamification-store";
import {
  syncCreateTask,
  syncUpdateTask,
  syncDeleteTask,
  syncCycleTaskStatus,
  syncAddSubtask,
  syncToggleSubtask,
  syncDeleteSubtask,
  syncReorderTask,
  getPendingTaskIds,
} from "../../../lib/api/db-sync";
import {
  migrateTask,
  updateTaskInArray,
  deleteTaskFromArray,
  cycleTaskStatus,
  addSubtaskToTask,
  toggleSubtaskInTask,
  deleteSubtaskFromTask,
  archiveTaskInArray,
  autoArchiveTasks,
  reorderTaskInArray,
} from "./task-store-helpers";

const STORAGE_KEY = "palnia_tasks";

function loadTasks(): Task[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(migrateTask);
  } catch {
    return [];
  }
}

let tasksCache: Task[] = loadTasks();
const listeners = new Set<() => void>();
const undoStack: Task[][] = [];
const redoStack: Task[][] = [];

function emitChange(skipUndo = false) {
  if (!skipUndo) {
    undoStack.push([...tasksCache]);
    if (undoStack.length > 50) undoStack.shift();
    redoStack.length = 0;
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tasksCache));
  for (const listener of listeners) { try { listener(); } catch { /* ignore stale listener during teardown */ } }
}

export function subscribe(listener: () => void) {
  listeners.add(listener);
  function handleStorageChange(event: StorageEvent) {
    if (event.key === STORAGE_KEY) {
      tasksCache = loadTasks();
      emitChange(true);
    }
  }
  window.addEventListener("storage", handleStorageChange);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", handleStorageChange);
  };
}

export function getSnapshot(): Task[] {
  return tasksCache;
}

export function addTask(
  title: string,
  category: Category,
  dueDate: string | null = null,
  priority: Priority = "normal",
  reminderMinutes: number | null = null,
  reminderTime: string | null = null,
  reminderType: "BEFORE" | "AT_TIME" | null = null,
) {
  const task = { ...createTask(title, category, dueDate, null, priority), reminderMinutes, reminderTime, reminderType };
  tasksCache = [task, ...tasksCache];
  emitChange();
  syncCreateTask(task);
  track("task_created", { category, has_due_date: dueDate !== null, priority, has_reminder: reminderMinutes !== null || reminderTime !== null });
}

export function addTaskForEvent(
  title: string,
  category: Category,
  eventId: EventId,
  dueDate: string | null = null,
  priority: Priority = "normal",
) {
  const task = createTask(title, category, dueDate, eventId, priority);
  tasksCache = [task, ...tasksCache];
  emitChange();
  syncCreateTask(task);
}

export function updateTask(
  id: TaskId,
  updates: Partial<
    Pick<Task, "title" | "status" | "dueDate" | "priority" | "notes" | "tags" | "eventId" | "reminderMinutes" | "reminderTime" | "reminderType" | "category">
  >,
) {
  tasksCache = updateTaskInArray(tasksCache, id, updates);
  emitChange();
  syncUpdateTask(id, { ...updates });
  track("task_updated", { fields: Object.keys(updates) });
}

export function deleteTask(id: TaskId) {
  tasksCache = deleteTaskFromArray(tasksCache, id);
  emitChange();
  syncDeleteTask(id);
}

export function deleteTaskLocal(id: TaskId) {
  undoStack.push([...tasksCache]);
  if (undoStack.length > 50) undoStack.shift();
  redoStack.length = 0;
  tasksCache = deleteTaskFromArray(tasksCache, id);
  emitChange(true);
}

export function confirmDeleteTask(id: TaskId) {
  syncDeleteTask(id);
}

export function toggleTaskDone(id: TaskId) {
  const task = tasksCache.find((t) => t.id === id);
  if (!task) return;
  const wasDone = task.status === "done";
  const newStatus = wasDone ? "todo" : "done";
  const completedAt = newStatus === "done" ? Date.now() : null;
  tasksCache = updateTaskInArray(tasksCache, id, { status: newStatus });
  // Mettre à jour completedAt localement
  tasksCache = tasksCache.map((t) => t.id === id ? { ...t, completedAt } : t);
  emitChange();
  syncUpdateTask(id, { status: newStatus });
  if (newStatus === "done" && !wasDone) {
    incrementTaskCompleted();
    checkTimeAchievement();
  }
}

export function cycleStatus(id: TaskId) {
  const before = tasksCache.find((t) => t.id === id);
  tasksCache = cycleTaskStatus(tasksCache, id);
  emitChange();
  syncCycleTaskStatus(id);
  const after = tasksCache.find((t) => t.id === id);
  if (after) {
    track("task_status_changed", { new_status: after.status });
    if (after.status === "done" && before?.status !== "done") {
      incrementTaskCompleted();
      checkTimeAchievement();
    }
  } else if (before) {
    track("task_status_changed", { new_status: "cycled" });
  }
}

export function addSubtask(taskId: TaskId, title: string) {
  tasksCache = addSubtaskToTask(tasksCache, taskId, title);
  emitChange();
  const task = tasksCache.find((t) => t.id === taskId);
  const newSub = task?.subtasks[task.subtasks.length - 1];
  if (newSub) syncAddSubtask(taskId, newSub);
  track("subtask_added", {});
}

export function toggleSubtask(taskId: TaskId, subtaskId: SubtaskId) {
  tasksCache = toggleSubtaskInTask(tasksCache, taskId, subtaskId);
  emitChange();
  syncToggleSubtask(taskId, subtaskId);
  track("subtask_toggled", {});
}

export function deleteSubtask(taskId: TaskId, subtaskId: SubtaskId) {
  tasksCache = deleteSubtaskFromTask(tasksCache, taskId, subtaskId);
  emitChange();
  syncDeleteSubtask(taskId, subtaskId);
}

export function archiveTask(id: TaskId) {
  tasksCache = archiveTaskInArray(tasksCache, id);
  emitChange();
  syncUpdateTask(id, { archived: true });
}

export function autoArchive() {
  const { tasks, changed } = autoArchiveTasks(tasksCache);
  tasksCache = tasks;
  if (changed) emitChange(true);
}

export function undo() {
  const prev = undoStack.pop();
  if (!prev) return;
  redoStack.push([...tasksCache]);
  tasksCache = prev;
  emitChange(true);
  track("undo_used", { scope: "task" });
}

export function redo() {
  const next = redoStack.pop();
  if (!next) return;
  undoStack.push([...tasksCache]);
  tasksCache = next;
  emitChange(true);
  track("redo_used", { scope: "task" });
}

export function reorderTask(taskId: TaskId, newIndex: number) {
  const result = reorderTaskInArray(tasksCache, taskId, newIndex);
  if (result) {
    tasksCache = result;
    emitChange();
    syncReorderTask(taskId, newIndex);
    track("task_reordered", {});
  }
}

export function importTasks(data: unknown[]) {
  tasksCache = data.map(migrateTask);
  emitChange();
}

export function canUndo(): boolean {
  return undoStack.length > 0;
}

export function canRedo(): boolean {
  return redoStack.length > 0;
}

export function resetFromServer(rawTasks: unknown[]): void {
  const serverTasks = rawTasks.map(migrateTask);
  const serverIds = new Set(serverTasks.map((t) => t.id));
  const pendingIds = getPendingTaskIds();

  // Preserve local tasks that are pending sync (not yet confirmed by server)
  const pendingLocal = tasksCache.filter(
    (t) => pendingIds.has(t.id) && !serverIds.has(t.id),
  );

  tasksCache = [...serverTasks, ...pendingLocal];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tasksCache));
  for (const listener of listeners) { try { listener(); } catch { /* stale */ } }
}

autoArchive();
